<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import StaticPicture from "../../../shared/components/Picture/StaticPicture.svelte";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./Contact.translations.json";
	import addressTranslations from "../../../shared/components/Address.translations.json";
	import type { ContactProps } from "./ContactProps.js";
	import contactHero from "../assets/images/contact/vertiflex-ostrava-poruba.jpeg";
	import contactHeroSm from "../assets/images/contact/vertiflex-ostrava-poruba.jpeg?webp&w=547&imagetools";
	import contactHeroMd from "../assets/images/contact/vertiflex-ostrava-poruba.jpeg?webp&w=600&imagetools";
	import contactHeroLg from "../assets/images/contact/vertiflex-ostrava-poruba.jpeg?webp&w=480&imagetools";
	import contactHeroXl from "../assets/images/contact/vertiflex-ostrava-poruba.jpeg?webp&w=707&imagetools";
	import contactHeroPl from "../assets/images/contact/vertiflex-poland.jpg";
	import contactHeroPlSm from "../assets/images/contact/vertiflex-poland.jpg?webp&w=547&imagetools";
	import contactHeroPlMd from "../assets/images/contact/vertiflex-poland.jpg?webp&w=600&imagetools";
	import contactHeroPlLg from "../assets/images/contact/vertiflex-poland.jpg?webp&w=480&imagetools";
	import contactHeroPlXl from "../assets/images/contact/vertiflex-poland.jpg?webp&w=707&imagetools";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import { Department } from "../../../../core/schema/Department.js";
	import ContactCardWrapper from "../../../shared/components/Contact/ContactCardWrapper.svelte";
	import type { Contact } from "../../../../core/schema/Contact.js";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import { setContext } from "svelte";
	import { getGlobalSettings } from "../getGlobalSettings";
	import { Locale } from "../../../../core/schema/Locale";

	type $$Props = ContactProps;

	const { contacts } = $$restProps as $$Props;
	const { TENANT_LOCALE } = getGlobalSettings();

	const translate = getTranslate(translations);
	const addressTranslate = getTranslate(addressTranslations);

	interface ContactData {
		contacts: Contact[];
		i18nKey: "backoffice" | "management" | "sales" | "serviceAndSupport" | "software";
	}

	$: groupedContacts = [
		{
			contacts: contacts.filter((contact) => contact.department === Department.Management),
			i18nKey: "management",
		},
		{
			contacts: contacts.filter((contact) => contact.department === Department.Sales),
			i18nKey: "sales",
		},
		{
			contacts: contacts.filter((contact) => contact.department === Department.Service),
			i18nKey: "serviceAndSupport",
		},
		{
			contacts: contacts.filter((contact) => contact.department === Department.Software),
			i18nKey: "software",
		},
		{
			contacts: contacts.filter((contact) => contact.department === Department.Backoffice),
			i18nKey: "backoffice",
		},
	] satisfies ContactData[];

	setContext(thumbnailImageContextKey, TENANT_LOCALE === Locale.pl ? contactHeroPl : contactHero);
</script>

<Layout>
	<div class="container">
		<section class="flex flex-col gap-2 lg:mb-8 lg:flex-row">
			<div class="hidden w-full lg:block lg:w-1/2">
				<StaticPicture
					image={TENANT_LOCALE === Locale.pl ? contactHeroPlXl : contactHeroXl}
					class="relative h-[20rem] rounded-lg sm:h-[25rem] lg:h-full"
					imgClass="rounded-lg h-full w-full object-cover inset-0 max-w-[initial] absolute"
					width={708}
					height={640}
					alt={translate("ilustationPhotoAltText")}
					loading="eager"
					fetchpriority="high"
					preload
					sourcesets={TENANT_LOCALE === Locale.pl
						? {
								"<md": contactHeroPlSm,
								">md": contactHeroPlMd,
								">lg": contactHeroPlLg,
								">xl": contactHeroPlXl,
							}
						: {
								"<md": contactHeroSm,
								">md": contactHeroMd,
								">lg": contactHeroLg,
								">xl": contactHeroXl,
							}}
				/>
			</div>
			<div class="flex flex-col lg:gap-[13rem] lg:p-4 xl:w-1/2 xl:p-8">
				<div class="p-2 pb-0 lg:p-0">
					<h1 class="text-2lg mb-[4.4375rem] p-0 font-normal lg:m-0 lg:text-3xl">
						{addressTranslate("headquartersCompanyName")}
					</h1>
				</div>
				<div class="flex flex-col gap-6 lg:flex-row xl:justify-between">
					<div class="grid grid-cols-2 p-2 pt-0 lg:mt-[-3rem] lg:flex lg:flex-col lg:p-0">
						<div class="flex flex-col">
							<span class="text-3xs md:text-xs lg:mb-0 lg:leading-[1.425rem]"
								>{addressTranslate("headquartersCompanyName")}</span
							>
							<a href={addressTranslate("headquartersGoogleMapsLink")} class="mb-4 lg:mb-0">
								<address>
									<span class="text-3xs block not-italic md:text-xs lg:mb-0 lg:leading-[1.425rem]"
										>{addressTranslate("headquartersStreet")}</span
									>
									<span class="text-3xs block not-italic md:text-xs lg:mb-0 lg:leading-[1.425rem]"
										>{addressTranslate("headquartersCity")}</span
									>
									<span class="text-3xs block not-italic md:text-xs lg:mb-8 lg:leading-[1.425rem]"
										>{translate("headquartersState")}</span
									>
								</address>
							</a>
							<div class="flex flex-col lg:mb-8">
								<span class="text-3xs md:text-xs lg:leading-[1.425rem]">
									{translate("cin")}
									{translate("cinValue")}
								</span>
								<span class="text-3xs md:text-xs lg:leading-[1.425rem]">
									{translate("vatId")}
									{translate("vatIdValue")}
								</span>
								{#if TENANT_LOCALE === Locale.pl}
									<span class="text-3xs md:text-xs lg:leading-[1.425rem]">
										{translate("krs")}
										{translate("krsValue")}
									</span>
								{/if}
							</div>
						</div>
						<div class="flex flex-col">
							<div class="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6">
								{#if TENANT_LOCALE !== Locale.pl}
									<span class="text-3xs mb-4 md:text-xs lg:mb-8 lg:leading-[1.425rem]">
										{translate("phone")}:
									</span>
									<a
										class="text-primary text-3xs col-span-2 mb-4 font-semibold sm:col-span-3 md:text-xs lg:col-span-5 lg:mb-8 lg:ml-2 lg:leading-[1.425rem]"
										href="tel:+420554818554"
									>
										{nbspify("+420 554 818 554")}
									</a>
								{/if}
								<span class="text-3xs text-nowrap md:text-xs lg:mb-0 lg:leading-[1.425rem]">{translate("email")}:</span>
								<span
									class="text-primary text-3xs col-span-2 justify-start font-semibold underline sm:col-span-3 md:text-xs lg:col-span-5 lg:mb-0 lg:ml-2 lg:leading-[1.425rem]"
								>
									<a href="mailto:{translate('emailValue')}">{translate("emailValue")}</a>
								</span>
								<span class="text-3xs md:text-xs lg:mb-0 lg:leading-[1.425rem]">
									{translate("sales")}:
								</span>
								<span
									class="text-primary text-3xs col-span-2 justify-start font-semibold underline sm:col-span-3 md:text-xs lg:col-span-5 lg:mb-0 lg:ml-2 lg:leading-[1.425rem]"
								>
									<a href="mailto:{translate('salesValue')}">{translate("salesValue")}</a>
								</span>
								<span class="text-3xs md:text-xs lg:mb-0 lg:leading-[1.425rem]">
									{translate("service")}:
								</span>
								<span
									class="text-primary text-3xs col-span-2 justify-start font-semibold underline sm:col-span-3 md:text-xs lg:col-span-5 lg:mb-0 lg:ml-2 lg:leading-[1.425rem]"
								>
									<a href="mailto:{translate('serviceValue')}">{translate("serviceValue")}</a>
								</span>
							</div>
						</div>
					</div>
					<StaticPicture
						image={TENANT_LOCALE === Locale.pl ? contactHeroPlXl : contactHeroXl}
						class="flex h-[16rem] w-full items-center justify-center overflow-hidden rounded-lg sm:h-auto lg:hidden"
						imgClass="w-full rounded-lg object-cover h-full object-center"
						width={304}
						height={192}
						alt={translate("ilustationPhotoAltText")}
						loading="eager"
						sourcesets={TENANT_LOCALE === Locale.pl
							? {
									"<md": contactHeroPlSm,
									">md": contactHeroPlMd,
									">lg": contactHeroPlLg,
									">xl": contactHeroPlXl,
								}
							: {
									"<md": contactHeroSm,
									">md": contactHeroMd,
									">lg": contactHeroLg,
									">xl": contactHeroXl,
								}}
					/>
				</div>
			</div>
		</section>
		<section>
			<div class="mt-12 flex flex-col gap-16 md:gap-20">
				{#each groupedContacts as { i18nKey, contacts }}
					<ContactCardWrapper key={i18nKey} headline={translate(i18nKey)} {contacts} />
				{/each}
			</div>
		</section>
	</div>
</Layout>
